/** Ardealy's own running costs (super-admin), as served by GET /services/pms/admin/costs. */

export const COST_PROVIDERS = ['openrouter', 'fal', 'vast', 'runpod', 'azure'] as const;
export type CostProviderId = (typeof COST_PROVIDERS)[number];
export type CostDays = 7 | 30 | 90;
export type CostLevel = 'ok' | 'low' | 'critical' | 'unknown';

export interface CostLine {
  key: string;
  label: string;
  usd: number;
  group?: string;
  quantity?: number;
  unit?: string;
  requests?: number;
}

export interface RunningItem {
  key: string;
  label: string;
  usdPerHour: number;
  detail?: string;
}

export interface ProviderKey {
  key: string;
  label: string;
  disabled: boolean;
  limitUsd: number | null;
  limitRemainingUsd: number | null;
  limitReset: string | null;
  usageTodayUsd: number;
  usageMonthUsd: number;
  usageTotalUsd: number;
}

export interface ProviderView {
  provider: CostProviderId;
  status: 'ok' | 'partial' | 'unconfigured' | 'error';
  error?: string;
  missing?: string[];
  fetchedAt: string;
  currency: string;
  balance: number | null;
  balanceUsd: number | null;
  balanceSource: 'provider' | 'manual' | null;
  purchasedUsd?: number | null;
  burnPerHourUsd: number | null;
  autoReload?: { amountUsd: number; thresholdUsd: number } | null;
  spendLimitUsd?: number | null;
  running?: RunningItem[];
  keys?: ProviderKey[];
  notes?: string[];
  level: CostLevel;
  levelReason?: string | null;
  thresholdUsd: number | null;
  budgetUsd: number | null;
  todayUsd: number | null;
  yesterdayUsd: number;
  monthUsd: number;
  prevMonthUsd: number;
  prevMonthToDateUsd: number;
  windowUsd: number;
  prevWindowUsd: number;
  avgDailyUsd: number;
  runwayDays: number | null;
  forecastMonthUsd: number;
  topUpUrl: string;
  trend: number[];
  balanceTrend: { at: string; usd: number }[];
  historyFrom: string | null;
  historySyncedAt: string | null;
}

export interface CostEvent {
  at: string;
  provider: CostProviderId;
  kind: 'topup' | 'alert';
  amountUsd?: number;
  type?: string;
}

export interface ProviderSettings {
  lowBalanceUsd?: number | null;
  monthlyBudgetUsd?: number | null;
}

export interface AzureCredit {
  amount: number;
  currency: string;
  startsAt: string;
  expiresAt?: string | null;
}

export interface CostSettings {
  alertsEnabled: boolean;
  monthlyBudgetUsd: number | null;
  providers: Record<CostProviderId, ProviderSettings>;
  azureCredit: AzureCredit | null;
}

export type CostSeriesPoint = { date: string; total: number } & Partial<Record<CostProviderId, number>>;

export interface CostsResponse {
  generatedAt: string;
  days: CostDays;
  fx: { eurUsd: number; source: 'ecb' | 'fallback'; at: string };
  totals: {
    todayUsd: number;
    yesterdayUsd: number;
    monthUsd: number;
    prevMonthToDateUsd: number;
    prevMonthUsd: number;
    forecastMonthUsd: number;
    budgetUsd: number | null;
    windowUsd: number;
    prevWindowUsd: number;
    burnPerHourUsd: number;
    meteredPerHourUsd: number;
    balanceUsd: number;
    lowestRunway: { provider: CostProviderId; days: number } | null;
    attention: number;
  };
  providers: ProviderView[];
  series: CostSeriesPoint[];
  breakdown: Record<CostProviderId, CostLine[]>;
  events: CostEvent[];
  ledger: { providerMonthUsd: number; attributedMonthUsd: number; byFeature: { feature: string; usd: number; calls: number }[] };
  settings: CostSettings;
}

/** Display names; the same in every language. */
export const PROVIDER_NAMES: Record<CostProviderId, string> = {
  openrouter: 'OpenRouter',
  fal: 'fal.ai',
  vast: 'vast.ai',
  runpod: 'RunPod',
  azure: 'Azure',
};
